import React, { useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faH1,
  faH2,
  faH3,
  faItalic,
  faListOl,
  faListUl,
  faImage,
  faLink,
  faBracketsCurly,
} from "@fortawesome/pro-solid-svg-icons";
import Markdown from "components/markdown/Markdown";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./textEditor.scss";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { IEmailTemplateVariable } from "api/rpc/2024-04/facilityAdmin/facility/emailTemplate";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import ReactDOM from "react-dom";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { AdminType, StatusCode } from "api/protocols";
import { PostMarkdownImage } from "api/rpc/2024-04/markdownImage";
import { moveMarkdownImage, removeMarkdownImage, setMarkdownImageWidth } from "components/markdown/imageWidth";
import { UserType } from "redux/reducers/models/user";

interface ITextEditorProps {
  markdownText: string;
  markdownTextOnChange: (arg0: string) => void;
  label?: string;
  readOnly?: boolean;
  error?: boolean;
  disabled?: boolean;
  variables?: Array<IEmailTemplateVariable>;
}

interface ITextEditor {
  writeOptionSelected: boolean;
  markdownRef: React.MutableRefObject<HTMLTextAreaElement>;
  markdownSelection: { time: number; selectionStart: number; selectionEnd?: number };
}

interface IMarkdownListType {
  type: string;
  syntax: string;
  regex: RegExp;
}

interface IMarkdownListTypes {
  unorderedList: IMarkdownListType;
  orderedList: IMarkdownListType;
}

enum MarkdownHeadingSyntax {
  H1 = "# ",
  H2 = "## ",
  H3 = "### ",
}

// Sample data for previewing customer variables
const PREVIEW_CUSTOMER = {
  first_name: "John",
  last_name: "Doe",
  full_name: "John Doe",
  email: "john.doe@email.com",
  phone: "(555) 123-4567",
};

function imageFilesFromTransfer(data: DataTransfer | null) {
  if (!data) {
    return [];
  }

  const files = Array.from(data.files).filter(file => file.type.startsWith("image/"));
  if (files.length > 0) {
    return files;
  }

  return Array.from(data.items)
    .filter(item => item.kind === "file" && item.type.startsWith("image/"))
    .map(item => item.getAsFile())
    .filter((file): file is File => Boolean(file));
}

function dragHasFile(data: DataTransfer | null) {
  if (!data) {
    return false;
  }

  if (Array.from(data.items).some(item => item.kind === "file")) {
    return true;
  }

  return data.files.length > 0;
}

function imageAltText(file: File) {
  const name = file.name?.replace(/\.[^.]+$/, "") || "image";
  return name.replace(/[[\]]/g, "").trim() || "image";
}

export default function TextEditor(props: ITextEditorProps) {
  const { facilityStore, authStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [imageDropActive, setImageDropActive] = useState(false);
  const markdownValueRef = useRef(props.markdownText);
  markdownValueRef.current = props.markdownText;
  const variablesDropdownRef = useRef<HTMLDivElement>(null);
  const [textEditorState, setTextEditorState] = useState<ITextEditor>({
    writeOptionSelected: true,
    markdownRef: useRef<HTMLTextAreaElement>(null),
    markdownSelection: { time: new Date().getTime(), selectionStart: 0 },
  });

  const [variablesDownDown, setVariablesDropDown] = useState<boolean>(false);

  const markdownListTypes: IMarkdownListTypes = {
    unorderedList: { type: "unorderedList", syntax: "- ", regex: /^- / },
    orderedList: { type: "orderedList", syntax: "1. ", regex: /^(\d+)\. / },
  };
  const { t } = useTranslation();

  function handleMarkdownTextChange(text: string) {
    markdownValueRef.current = text;
    if (!props.readOnly) {
      props.markdownTextOnChange(text);
    }
  }

  function adminTypeForUpload() {
    switch (authStore?.user?.user_type) {
      case UserType.CLIENT_ADMIN:
        return AdminType.ClientAdmin;
      case UserType.MASTER_ADMIN:
        return AdminType.MasterAdmin;
      default:
        return AdminType.FacilityAdmin;
    }
  }

  async function uploadMarkdownImages(files: File[]) {
    if (props.readOnly || props.disabled || files.length === 0) {
      return;
    }

    const textarea = textEditorState.markdownRef.current;
    const insertAtCursor = textarea != null && document.activeElement === textarea;
    const selectionStart = insertAtCursor ? textarea.selectionStart : (markdownValueRef.current ?? "").length;
    const selectionEnd = insertAtCursor ? textarea.selectionEnd : selectionStart;
    const uploads = files.map(file => ({
      file,
      token: `uploading-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      alt: imageAltText(file),
    }));
    const placeholder = uploads.map(upload => `![Uploading ${upload.alt}…](${upload.token})`).join("\n");
    const current = markdownValueRef.current ?? "";
    handleMarkdownTextChange(current.substring(0, selectionStart) + placeholder + current.substring(selectionEnd));
    setTextEditorState(prevState => ({
      ...prevState,
      markdownSelection: {
        time: new Date().getTime(),
        selectionStart: selectionStart + placeholder.length,
        selectionEnd: selectionStart + placeholder.length,
      },
    }));

    for (const upload of uploads) {
      const response = await PostMarkdownImage(upload.file, adminTypeForUpload());
      const uploadedUrl = response.status === StatusCode.OK ? response.data?.source : undefined;
      const replacement = uploadedUrl ? `![${upload.alt}](${uploadedUrl})` : "";
      const latest = markdownValueRef.current ?? "";
      handleMarkdownTextChange(latest.replace(`![Uploading ${upload.alt}…](${upload.token})`, replacement));

      if (!uploadedUrl) {
        dispatch(showError(typeof response.data === "string" ? response.data : t("Failed to upload image")));
      }
    }
  }

  function handleImageResize(src: string, alt: string | undefined, width: number | null) {
    handleMarkdownTextChange(setMarkdownImageWidth(markdownValueRef.current ?? "", src, alt, width));
  }

  function handleImageRemove(src: string, alt: string | undefined) {
    handleMarkdownTextChange(removeMarkdownImage(markdownValueRef.current ?? "", src, alt));
  }

  function handleImageMove(src: string, alt: string | undefined, insertAt: number) {
    handleMarkdownTextChange(moveMarkdownImage(markdownValueRef.current ?? "", src, alt, insertAt));
  }

  function handlePaste(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = imageFilesFromTransfer(event.clipboardData);
    if (files.length === 0) {
      return;
    }

    event.preventDefault();
    void uploadMarkdownImages(files);
  }

  function handleEditorDragEnter(event: React.DragEvent<HTMLDivElement>) {
    if (props.readOnly || props.disabled || !dragHasFile(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setImageDropActive(true);
  }

  function handleEditorDragOver(event: React.DragEvent<HTMLDivElement>) {
    if (props.readOnly || props.disabled || !dragHasFile(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  function handleEditorDragLeave(event: React.DragEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setImageDropActive(false);
  }

  function handleEditorDrop(event: React.DragEvent<HTMLDivElement>) {
    setImageDropActive(false);

    const files = imageFilesFromTransfer(event.dataTransfer);
    if (files.length === 0 || props.readOnly || props.disabled) {
      return;
    }

    event.preventDefault();
    void uploadMarkdownImages(files);
  }

  useLayoutEffect(() => {
    if (!props.readOnly) {
      let selectionStart = textEditorState.markdownSelection.selectionStart;
      let selectionEnd =
        textEditorState.markdownSelection.selectionEnd ?? textEditorState.markdownSelection.selectionStart;

      selectionStart = selectionStart < 0 ? 0 : selectionStart;
      selectionEnd = selectionEnd < 0 ? 0 : selectionEnd;

      if (textEditorState.markdownRef?.current) {
        textEditorState.markdownRef.current.selectionStart = selectionStart;
        textEditorState.markdownRef.current.selectionEnd = selectionEnd;
      }
    }
  }, [textEditorState.markdownSelection]);

  function handleViewOptionChange(viewOption: boolean) {
    setTextEditorState(prevState => {
      return { ...prevState, writeOptionSelected: viewOption };
    });
  }

  function handleEditorContentChange(event: any) {
    const markdownText = event?.target?.value;

    if (markdownText === undefined) {
      return;
    }

    handleMarkdownTextChange(String(markdownText));
  }

  function getStartOfLinePosition(selection: number) {
    const lineNumber = props.markdownText.substring(0, selection).split("\n").length;
    let startOfLinePosition = 0;

    for (let i = 0; i < lineNumber - 1; i++) {
      startOfLinePosition = props.markdownText.indexOf("\n", startOfLinePosition) + 1;
    }

    return startOfLinePosition;
  }

  function getCurrentLineString(selection: number) {
    const startOfLinePosition = getStartOfLinePosition(selection);
    let startOfNextLinePosition = props.markdownText.indexOf("\n", startOfLinePosition);
    if (startOfNextLinePosition === -1) {
      startOfNextLinePosition = props.markdownText.length;
    }
    const currentLineString = props.markdownText.substring(startOfLinePosition, startOfNextLinePosition);
    return currentLineString;
  }

  function getPreviousLineString(selection: number) {
    const startOfLinePosition = getStartOfLinePosition(selection);
    let startOfPreviousLine = startOfLinePosition - 1;

    while (
      props.markdownText[startOfPreviousLine - 1] !== undefined &&
      props.markdownText[startOfPreviousLine - 1] !== "\n"
    ) {
      startOfPreviousLine--;
    }

    const previousLine = props.markdownText.substring(startOfPreviousLine, startOfLinePosition);
    return previousLine;
  }

  function handleEditorContentKeydown(event: any) {
    const key = event?.key;
    const ctrlKeySelected = event?.ctrlKey;

    if (key === undefined) {
      return;
    }

    if (ctrlKeySelected && key === "b") {
      event.preventDefault();
      addBoldText();
    } else if (ctrlKeySelected && key === "i") {
      event.preventDefault();
      addItalicText();
    } else if (ctrlKeySelected && key === "1") {
      event.preventDefault();
      addList(markdownListTypes.unorderedList);
    } else if (ctrlKeySelected && key === "2") {
      event.preventDefault();
      addList(markdownListTypes.orderedList);
    } else if (key === "Enter") {
      const selectionStart = textEditorState?.markdownRef?.current?.selectionStart;
      const startOfLinePosition = getStartOfLinePosition(selectionStart);
      const currentLineString = getCurrentLineString(selectionStart);

      const markdownListTypeValues: IMarkdownListType[] = Object.values(markdownListTypes);
      let markdownListTypeMatched: IMarkdownListType = undefined;

      let orderedNumber: string = undefined;
      let currentOrderedNumberSyntax: string = undefined;
      let nextOrderedNumberSyntax: string = undefined;

      markdownListTypeValues.map((markdownListType: IMarkdownListType) => {
        if (markdownListType.regex.test(currentLineString)) {
          markdownListTypeMatched = markdownListType;
        }
      });

      if (markdownListTypeMatched?.type === "orderedList") {
        orderedNumber = markdownListTypeMatched.regex.exec(currentLineString)[1];
        currentOrderedNumberSyntax = String(parseInt(orderedNumber)) + ". ";
        nextOrderedNumberSyntax = String(parseInt(orderedNumber) + 1) + ". ";
      }

      const currentSyntaxLength = !orderedNumber
        ? markdownListTypeMatched?.syntax?.length
        : currentOrderedNumberSyntax.length;
      const currentLineAfterSyntax = currentLineString.substring(currentSyntaxLength);

      if (markdownListTypeMatched && selectionStart >= startOfLinePosition + currentSyntaxLength) {
        event.preventDefault();

        if (currentLineAfterSyntax.trim() === "") {
          const updatedMarkdownText =
            props.markdownText.substring(0, startOfLinePosition) +
            props.markdownText.substring(startOfLinePosition + currentSyntaxLength);

          handleMarkdownTextChange(updatedMarkdownText);
          setTextEditorState(prevState => {
            return {
              ...prevState,
              markdownSelection: { time: new Date().getTime(), selectionStart: startOfLinePosition },
            };
          });
        } else {
          let updatedMarkdownText = "";

          if (markdownListTypeMatched.type === "unorderedList") {
            updatedMarkdownText =
              props.markdownText.substring(0, selectionStart) +
              "\n" +
              markdownListTypeMatched.syntax +
              props.markdownText.substring(selectionStart);

            handleMarkdownTextChange(updatedMarkdownText);
            setTextEditorState(prevState => {
              return {
                ...prevState,
                markdownSelection: {
                  time: new Date().getTime(),
                  selectionStart: selectionStart + markdownListTypeMatched.syntax.length + 1,
                },
              };
            });
          } else if (markdownListTypeMatched.type === "orderedList") {
            updatedMarkdownText =
              props.markdownText.substring(0, selectionStart) +
              "\n" +
              nextOrderedNumberSyntax +
              props.markdownText.substring(selectionStart);

            handleMarkdownTextChange(updatedMarkdownText);
            setTextEditorState(prevState => {
              return {
                ...prevState,
                markdownSelection: {
                  time: new Date().getTime(),
                  selectionStart: selectionStart + nextOrderedNumberSyntax.length + 1,
                },
              };
            });
          }
        }
      }
    }
  }

  function addHeading(syntax: string) {
    const selectionStart = textEditorState?.markdownRef?.current?.selectionStart;
    const syntaxLength = syntax.length;
    let headingStart = selectionStart;

    if (selectionStart === undefined) {
      return;
    }

    while (
      props.markdownText[headingStart - 1] !== " " &&
      props.markdownText[headingStart - 1] !== undefined &&
      props.markdownText[headingStart - 1] !== "\n"
    ) {
      headingStart--;
    }

    const updatedMarkdownText =
      props.markdownText.substring(0, headingStart) + syntax + props.markdownText.substring(headingStart);

    handleMarkdownTextChange(updatedMarkdownText);
    setTextEditorState(prevState => {
      return {
        ...prevState,
        markdownSelection: {
          time: new Date().getTime(),
          selectionStart: selectionStart + syntaxLength,
        },
      };
    });

    textEditorState.markdownRef.current.focus();
  }

  function addInlineStyle(syntax: string) {
    const selectionStart = textEditorState?.markdownRef?.current?.selectionStart;
    const selectionEnd = textEditorState?.markdownRef?.current?.selectionEnd;
    const syntaxLength = syntax.length;
    let updatedMarkdownText = "";

    if (selectionStart === undefined || selectionEnd === undefined) {
      return;
    }

    if (selectionStart === selectionEnd) {
      let styleStart = selectionStart;
      let styleEnd = selectionStart;

      while (
        props.markdownText[styleStart - 1] !== " " &&
        props.markdownText[styleStart - 1] !== undefined &&
        props.markdownText[styleStart - 1] !== "\n"
      ) {
        styleStart--;
      }
      while (
        props.markdownText[styleEnd] !== " " &&
        props.markdownText[styleEnd] !== undefined &&
        props.markdownText[styleEnd] !== "\n"
      ) {
        styleEnd++;
      }

      const potentialStartingSyntax = props.markdownText.substring(styleStart, styleStart + syntaxLength);
      const potentialEndingSyntax = props.markdownText.substring(styleEnd - syntaxLength, styleEnd);

      if (potentialStartingSyntax === syntax && potentialEndingSyntax === syntax) {
        updatedMarkdownText =
          props.markdownText.substring(0, styleStart) +
          props.markdownText.substring(styleStart + syntaxLength, styleEnd - syntaxLength) +
          props.markdownText.substring(styleEnd);

        handleMarkdownTextChange(updatedMarkdownText);
        setTextEditorState(prevState => {
          return {
            ...prevState,
            markdownSelection: {
              time: new Date().getTime(),
              selectionStart: Math.min(
                styleEnd - syntaxLength * 2,
                Math.max(styleStart, selectionStart - syntaxLength),
              ),
            },
          };
        });
      } else {
        updatedMarkdownText =
          props.markdownText.substring(0, styleStart) +
          syntax +
          props.markdownText.substring(styleStart, styleEnd) +
          syntax +
          props.markdownText.substring(styleEnd);

        handleMarkdownTextChange(updatedMarkdownText);
        setTextEditorState(prevState => {
          return {
            ...prevState,
            markdownSelection: {
              time: new Date().getTime(),
              selectionStart: selectionStart + syntaxLength,
            },
          };
        });
      }
    } else {
      const potentialOuterStartingIndex = selectionStart - syntaxLength;
      const potentialOuterEndingIndex = selectionEnd + syntaxLength;
      const potentialOuterStartingSyntax = props.markdownText.substring(potentialOuterStartingIndex, selectionStart);
      const potentialOuterEndingSyntax = props.markdownText.substring(selectionEnd, potentialOuterEndingIndex);

      const potentialInnerStartingIndex = selectionStart + syntaxLength;
      const potentialInnerEndingIndex = selectionEnd - syntaxLength;
      const potentialInnerStartingSyntax = props.markdownText.substring(selectionStart, potentialInnerStartingIndex);
      const potentialInnerEndingSyntax = props.markdownText.substring(potentialInnerEndingIndex, selectionEnd);

      if (potentialOuterStartingSyntax === syntax && potentialOuterEndingSyntax === syntax) {
        updatedMarkdownText =
          props.markdownText.substring(0, potentialOuterStartingIndex) +
          props.markdownText.substring(selectionStart, selectionEnd) +
          props.markdownText.substring(potentialOuterEndingIndex);

        handleMarkdownTextChange(updatedMarkdownText);
        setTextEditorState(prevState => {
          return {
            ...prevState,
            markdownSelection: {
              time: new Date().getTime(),
              selectionStart: selectionStart - syntaxLength,
              selectionEnd: selectionEnd - syntaxLength,
            },
          };
        });
      } else if (potentialInnerStartingSyntax === syntax && potentialInnerEndingSyntax === syntax) {
        updatedMarkdownText =
          props.markdownText.substring(0, selectionStart) +
          props.markdownText.substring(potentialInnerStartingIndex, potentialInnerEndingIndex) +
          props.markdownText.substring(selectionEnd);

        handleMarkdownTextChange(updatedMarkdownText);
        setTextEditorState(prevState => {
          return {
            ...prevState,
            markdownSelection: {
              time: new Date().getTime(),
              selectionStart: selectionStart,
              selectionEnd: selectionEnd - syntaxLength * 2,
            },
          };
        });
      } else {
        updatedMarkdownText =
          props.markdownText.substring(0, selectionStart) +
          syntax +
          props.markdownText.substring(selectionStart, selectionEnd) +
          syntax +
          props.markdownText.substring(selectionEnd);

        handleMarkdownTextChange(updatedMarkdownText);
        setTextEditorState(prevState => {
          return {
            ...prevState,
            markdownSelection: {
              time: new Date().getTime(),
              selectionStart: selectionStart + syntaxLength,
              selectionEnd: selectionEnd + syntaxLength,
            },
          };
        });
      }
    }

    textEditorState.markdownRef.current.focus();
  }

  function addBoldText() {
    addInlineStyle("**");
  }

  function addItalicText() {
    addInlineStyle("_");
  }

  function addList(listType: IMarkdownListType) {
    const selectionStart = textEditorState?.markdownRef?.current?.selectionStart;
    const syntax = listType.syntax;
    const syntaxLength = syntax.length;
    const markdownListTypeValues: IMarkdownListType[] = Object.values(markdownListTypes);
    let markdownListTypeMatched: IMarkdownListType = undefined;
    let previousLineIsAList = false;
    let updatedMarkdownText = "";

    if (selectionStart === undefined) {
      return;
    }

    const startOfLinePosition = getStartOfLinePosition(selectionStart);
    const currentLineString = getCurrentLineString(selectionStart);
    const previousLineString = getPreviousLineString(selectionStart);

    markdownListTypeValues.map((markdownListType: IMarkdownListType) => {
      if (markdownListType.regex.test(currentLineString)) {
        markdownListTypeMatched = markdownListType;
      }
    });

    if (markdownListTypeValues.some(markdownListType => markdownListType.regex.test(previousLineString))) {
      previousLineIsAList = true;
    }

    if (markdownListTypeMatched) {
      let orderedNumber: string = undefined;
      let currentOrderedNumberSyntax: string = undefined;

      if (markdownListTypeMatched.type === "orderedList") {
        orderedNumber = markdownListTypeMatched.regex.exec(currentLineString)[1];
        currentOrderedNumberSyntax = String(parseInt(orderedNumber)) + ". ";
      }

      const existingSyntaxLength = !orderedNumber
        ? markdownListTypeMatched.syntax.length
        : currentOrderedNumberSyntax.length;

      updatedMarkdownText =
        props.markdownText.substring(0, startOfLinePosition) +
        props.markdownText.substring(startOfLinePosition + existingSyntaxLength);

      handleMarkdownTextChange(updatedMarkdownText);
      setTextEditorState(prevState => {
        return {
          ...prevState,
          markdownSelection: {
            time: new Date().getTime(),
            selectionStart: Math.max(startOfLinePosition, selectionStart - existingSyntaxLength),
          },
        };
      });
    } else {
      updatedMarkdownText =
        props.markdownText.substring(0, startOfLinePosition) +
        (previousLineIsAList ? "\n" : "") +
        syntax +
        props.markdownText.substring(startOfLinePosition);

      handleMarkdownTextChange(updatedMarkdownText);
      setTextEditorState(prevState => {
        return {
          ...prevState,
          markdownSelection: {
            time: new Date().getTime(),
            selectionStart: selectionStart + syntaxLength + (previousLineIsAList ? 1 : 0),
          },
        };
      });
    }

    textEditorState.markdownRef.current.focus();
  }

  function addLink() {
    const selectionStart = textEditorState?.markdownRef?.current?.selectionStart;
    let updatedMarkdownText = "";

    if (selectionStart === undefined) {
      return;
    }

    updatedMarkdownText =
      props.markdownText.substring(0, selectionStart) +
      "[Link](https://www.teeon.com)" +
      props.markdownText.substring(selectionStart);

    handleMarkdownTextChange(updatedMarkdownText);
    setTextEditorState(prevState => {
      return {
        ...prevState,
        markdownSelection: {
          time: new Date().getTime(),
          selectionStart: selectionStart + 5,
        },
      };
    });

    textEditorState.markdownRef.current.focus();
  }

  function insertAtCursor(text: string) {
    const textarea = textEditorState.markdownRef.current;
    if (!textarea) {
      return;
    }

    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const updatedMarkdownText =
      props.markdownText.substring(0, selectionStart) + text + props.markdownText.substring(selectionEnd);

    ReactDOM.unstable_batchedUpdates(() => {
      handleMarkdownTextChange(updatedMarkdownText);
      setTextEditorState(prevState => ({
        ...prevState,
        markdownSelection: {
          time: new Date().getTime(),
          selectionStart: selectionStart + text.length,
          selectionEnd: selectionStart + text.length,
        },
      }));
      setVariablesDropDown(false);
    });

    textarea.focus();
  }

  useOutsideAlerter(variablesDropdownRef, () => {
    setVariablesDropDown(false);
  });

  function applyPreviewVariables(markdown: string, context: Record<string, any>) {
    if (!markdown) {
      return markdown;
    }

    let output = markdown;

    Object.entries(context).forEach(([key, value]) => {
      if (!value || typeof value !== "object") {
        return;
      }

      Object.entries(value as string).forEach(([field, fieldValue]) => {
        const regex = new RegExp(`{{${key}\\.${field}}}`, "g");
        output = output.replace(regex, String(fieldValue));
      });
    });

    return output;
  }

  return (
    <div>
      {props.label && <span className="text-editor-label">{props.label}</span>}
      <div
        className={classNames("text-editor", {
          "text-editor-error": props.error,
          "text-editor-drop-active": imageDropActive,
        })}
        style={props.label ? { marginTop: "2px" } : {}}
        onDragEnter={handleEditorDragEnter}
        onDragOver={handleEditorDragOver}
        onDragLeave={handleEditorDragLeave}
        onDrop={handleEditorDrop}
      >
        <div className="text-editor-header">
          <div>
            <button
              type="button"
              className={`text-editor-view-option ${
                textEditorState.writeOptionSelected ? "text-editor-view-option-active" : ""
              }`}
              onClick={() => handleViewOptionChange(true)}
            >
              {t("Write")}
            </button>
            <button
              type="button"
              className={`text-editor-view-option ${
                !textEditorState.writeOptionSelected ? "text-editor-view-option-active" : ""
              }`}
              onClick={() => handleViewOptionChange(false)}
            >
              {t("Preview")}
            </button>
          </div>
          {textEditorState.writeOptionSelected ? (
            <div>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="headingTooltip">{`${t("Add a primary header")}`}</Tooltip>}
              >
                <button
                  type="button"
                  className="text-editor-action"
                  onClick={() => addHeading(MarkdownHeadingSyntax.H1)}
                >
                  <FontAwesomeIcon icon={faH1} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="headingTooltip">{`${t("Add a secondary header")}`}</Tooltip>}
              >
                <button
                  type="button"
                  className="text-editor-action"
                  onClick={() => addHeading(MarkdownHeadingSyntax.H2)}
                >
                  <FontAwesomeIcon icon={faH2} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="headingTooltip">{`${t("Add a tertiary header")}`}</Tooltip>}
              >
                <button
                  type="button"
                  className="text-editor-action"
                  onClick={() => addHeading(MarkdownHeadingSyntax.H3)}
                >
                  <FontAwesomeIcon icon={faH3} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="boldTooltip">{`${t("Add bold text, <Ctrl+b>")}`}</Tooltip>}
              >
                <button type="button" className="text-editor-action" onClick={() => addBoldText()}>
                  <FontAwesomeIcon icon={faBold} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="italicTooltip">{`${t("Add italic text, <Ctrl+i>")}`}</Tooltip>}
              >
                <button type="button" className="text-editor-action" onClick={() => addItalicText()}>
                  <FontAwesomeIcon icon={faItalic} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="unorderedListTooltip">{`${t("Add an unordered list, <Ctrl+1>")}`}</Tooltip>}
              >
                <button
                  type="button"
                  className="text-editor-action"
                  onClick={() => addList(markdownListTypes.unorderedList)}
                >
                  <FontAwesomeIcon icon={faListUl} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="orderedListTooltip">{`${t("Add an ordered list, <Ctrl+2>")}`}</Tooltip>}
              >
                <button
                  type="button"
                  className="text-editor-action"
                  onClick={() => addList(markdownListTypes.orderedList)}
                >
                  <FontAwesomeIcon icon={faListOl} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="imageTooltip">{`${t("Add an image")}`}</Tooltip>}
              >
                <button
                  type="button"
                  className="text-editor-action"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={props.readOnly || props.disabled}
                >
                  <FontAwesomeIcon icon={faImage} />
                </button>
              </OverlayTrigger>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={event => {
                  const files = Array.from(event.target.files ?? []);
                  event.target.value = "";
                  void uploadMarkdownImages(files);
                }}
              />
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="imageTooltip">{`${t("Add a link")}`}</Tooltip>}>
                <button type="button" className="text-editor-action" onClick={() => addLink()}>
                  <FontAwesomeIcon icon={faLink} />
                </button>
              </OverlayTrigger>
              {props.variables ? (
                <div className="text-editor-variables" ref={variablesDropdownRef}>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id="imageTooltip">{`${t("Insert a variable")}`}</Tooltip>}
                  >
                    <button
                      type="button"
                      className="text-editor-action"
                      onClick={() => {
                        setVariablesDropDown(prev => !prev);
                        textEditorState?.markdownRef?.current?.focus();
                      }}
                    >
                      <FontAwesomeIcon icon={faBracketsCurly} />
                    </button>
                  </OverlayTrigger>
                  <div
                    className={classNames("text-editor-variables-container", {
                      "text-editor-variables-container-active": variablesDownDown,
                    })}
                    style={{
                      maxHeight: `${
                        window.innerHeight - variablesDropdownRef?.current?.getBoundingClientRect().bottom
                      }px`,
                    }}
                  >
                    {props?.variables
                      ?.filter(variableGroup =>
                        variableGroup?.group === "Client" ? facilityStore?.facilities?.length > 1 : true,
                      )
                      ?.map((variableGroup, index) => {
                        return (
                          <div key={index} className="">
                            <p className="text-editor-variables-group-title">{variableGroup?.group}</p>
                            {variableGroup?.variables?.map((variable, variableIndex) => {
                              return (
                                <div
                                  key={variableIndex}
                                  className="text-editor-variables-title"
                                  onClick={() => insertAtCursor(variable?.token)}
                                >
                                  {variable?.label}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="text-editor-main">
          {imageDropActive ? <div className="text-editor-drop-target">{t("Drop image to upload")}</div> : null}
          {textEditorState.writeOptionSelected ? (
            <textarea
              ref={textEditorState.markdownRef}
              className="text-editor-content"
              placeholder={t("Edit details")}
              value={props.markdownText}
              onChange={handleEditorContentChange}
              onKeyDown={handleEditorContentKeydown}
              onPaste={handlePaste}
              readOnly={props.readOnly}
              disabled={props.disabled}
            />
          ) : (
            <div className="text-editor-markdown-container">
              {props.markdownText === "" ? (
                <div>{t("Nothing to preview")}</div>
              ) : props.variables ? (
                <Markdown
                  markdownText={applyPreviewVariables(props.markdownText, {
                    facility: facilityStore?.facility,
                    client: facilityStore?.facility?.client,
                    customer: PREVIEW_CUSTOMER,
                  })}
                  onImageResize={props.readOnly || props.disabled ? undefined : handleImageResize}
                  onImageRemove={props.readOnly || props.disabled ? undefined : handleImageRemove}
                />
              ) : (
                <Markdown
                  markdownText={props.markdownText}
                  onImageResize={props.readOnly || props.disabled ? undefined : handleImageResize}
                  onImageRemove={props.readOnly || props.disabled ? undefined : handleImageRemove}
                  onImageMove={props.readOnly || props.disabled ? undefined : handleImageMove}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
